import { SuretyAccountId } from "@inrev/common";
import { useAdminFetchAccount } from "@portal/api/account/admin";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AccountLayout } from "../AccountLayout";
import { AdminAccountBondsTab } from "./tabs/bonds/AdminAccountBondsTab";
import { AdminAccountDetailsTab } from "./tabs/details/AdminAccountDetailsTab";
import { AdminAccountFinancialsWithAdjustmentsTab } from "./tabs/financials/AdminAccountFinancialsWithAdjustmentsTab";
import { AdminAccountIndemnityTab } from "./tabs/indemnity/AdminAccountIndemnityTab";
import { AdminAccountOverviewTab } from "./tabs/overview/AdminAccountOverviewTab";
import { AdminAccountWIPTab } from "./tabs/wip/AdminAccountWIPTab";

export const AdminSuretyAccountView = () => {
	const { id } = useParams();

	const { account, accountIsLoading, accountError } = useAdminFetchAccount(id as SuretyAccountId);
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTabId = useMemo(() => searchParams.get("t"), [searchParams]);
	const navigate = useNavigate();
	const wipExtractionEnabled = useFeatureFlag("wip_extraction_project");

	useEffect(() => {
		if (!activeTabId) {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("t", "overview");
					return next;
				},
				{ replace: true },
			);
		}
	}, []);

	useEffect(() => {
		if (accountError) {
			navigate("/accounts");
		}
	}, [accountError]);

	if (accountIsLoading || account === undefined) {
		return <LoadingModal />;
	}

	return (
		<AccountLayout
			account={account}
			tabs={[
				{
					id: "overview",
					label: "Overview",
					Component: AdminAccountOverviewTab,
					isDefault: activeTabId === null || activeTabId === "overview",
				},
				{
					id: "details",
					label: "Details",
					Component: AdminAccountDetailsTab,
					isDefault: activeTabId === "details",
				},
				{
					id: "financials",
					label: "Financials",
					Component: AdminAccountFinancialsWithAdjustmentsTab,
					isDefault: activeTabId === "financials",
					expanded: true,
					viewport: true,
				},
				{
					id: "wip",
					label: "WIP",
					Component: AdminAccountWIPTab,
					isDefault: activeTabId === "wip",
					expanded: wipExtractionEnabled,
					viewport: wipExtractionEnabled,
				},
				{
					id: "indemnity",
					label: "Indemnity",
					Component: AdminAccountIndemnityTab,
					isDefault: activeTabId === "indemnity",
				},
				{
					id: "bonds",
					label: "Bonds",
					Component: AdminAccountBondsTab,
					isDefault: activeTabId === "bonds",
					expanded: true,
				},
			]}
		/>
	);
};
